import { useAnalytics } from '@pco/analytics';
import { useAddProductsToCartMutation } from '@pco/api/generated/graphql';
import type { Vehicle } from '@pco/modules/VehicleSelection';
import type { ProductCardView } from '@pco/molecules/ProductCard/index.types';
import { useCartStore } from '@pco/stores/cart/cart.store';
import type { CartState } from '@pco/stores/cart/cart.types';
import type { Nullish } from '@pco/types';
import type { ProductAttribute } from '@pco/types/product';
import { getFitmentType } from '@pco/utils/useGetFitment';
import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

interface ProductToSave {
	sku: string;
	quantity: number;
	fitment_status: string;
	vehicle_name: string;
	vin_number: string;
}

type CartItem = {
	productView: ProductCardView;
	quantity: number;
	openDrawer?: boolean;
	vehicle: Nullish<Vehicle>;
};

type FitmentResult = 'ExactFit' | 'MayNotFit' | 'CompatibleWithMost';

const fitmentMapping: Record<FitmentResult, string> = {
	ExactFit: '2',
	MayNotFit: '3',
	CompatibleWithMost: '0',
};

const getFitmentStatus = (
	attributes: ProductAttribute[],
	vehicle?: Vehicle | null,
) => {
	const fitmentResult = getFitmentType(attributes, vehicle) as FitmentResult;
	return fitmentMapping[fitmentResult] ?? '1';
};

function filterProductsView(
	productView: ProductCardView,
	quantity: number,
	vehicle: Nullish<Vehicle>,
): ProductToSave[] {
	if (productView?.type === 'bundle' && productView.options?.[0]?.values) {
		const products = productView.options[0].values
			.flatMap((product) => {
				if (product.__typename === 'ProductViewOptionValueProduct') {
					const fitment_status = getFitmentStatus(
						product?.product?.attributes as ProductAttribute[],
						vehicle,
					);

					return {
						sku: product.product?.sku ?? '',
						quantity: (product.quantity ?? 0) * quantity,
						fitment_status,
						vehicle_name: `${vehicle?.year ?? ''} ${vehicle?.model ?? ''}`,
						vin_number: vehicle?.vin,
					};
				}
				return []; // Explicitly return empty array for non-matching types
			})
			.filter(
				(product): product is ProductToSave =>
					!!product && product.quantity > 0 && product.sku.length > 0,
			);

		if (products.length > 0) {
			return products;
		}
	}

	// Default fallback
	const fitment_status = getFitmentStatus(productView?.attributes, vehicle);
	return [
		{
			sku: productView?.sku ?? '',
			quantity,
			fitment_status,
			vehicle_name: `${vehicle?.year ?? ''} ${vehicle?.model ?? ''}`,
			vin_number: vehicle?.vin ?? '',
		},
	];
}

function getProductFormCart(
	cart: CartState,
	items: { sku: string; quantity: number },
) {
	return cart.itemsV2?.items?.find((item) => item?.product.sku === items.sku);
}

const queueRef: CartItem[] = [];
let queueStatusRef = false;

export const useAddProductToCart = () => {
	const {
		cartId,
		addProductToCart,
		addProductToCartSuccess,
		setShowAddToCartError,
		_fetchCart,
	} = useCartStore();
	const queryClient = useQueryClient();
	const productRef = useRef<{
		productView: ProductCardView | null;
		productItems: {
			sku: string;
			quantity: number;
		}[];
		openDrawer: boolean;
	}>({
		productView: null,
		openDrawer: true,
		productItems: [],
	});
	const fireAnalytics = useAnalytics('Cart');

	const addProductsMutation = useAddProductsToCartMutation({
		scope: {
			id: 'addToCart',
		},
		onSuccess: (data, variables) => {
			const productView = productRef.current.productView;
			const productItems = productRef.current.productItems;
			const item = Array.isArray(variables?.items)
				? variables.items[0]
				: variables?.items;
			const addedItem = getProductFormCart(
				data.addProductsToCart?.cart as CartState,
				{ sku: item?.sku ?? '', quantity: item?.quantity ?? 0 },
			);
			if (!addedItem?.quantity) {
				setShowAddToCartError({
					isCartError: true,
					errorMessage:
						'Product not added to cart. Item was not found in returned cart data.',
				});

				addProductToCartSuccess();
				throw new Error('Can not add product');
			}
			if (
				data.addProductsToCart?.cart &&
				productRef.current.openDrawer &&
				productView
			) {
				productRef.current.productView &&
					addProductToCart({
						product: productRef.current.productView,
						quantity: addedItem?.quantity,
						isAdded: addedItem?.quantity === productItems[0].quantity,
					});
			}

			addProductToCartSuccess();
			setShowAddToCartError({ isCartError: false });
			fireAnalytics('addItem', { success: true, productView });
			if (data.addProductsToCart?.cart) {
				queryClient.invalidateQueries({
					queryKey: ['Cart'],
					refetchType: 'active',
				});
				_fetchCart(data.addProductsToCart?.cart as CartState);
			}
			queueStatusRef = false;
			if (queueRef.length > 0) {
				const nextData = queueRef.shift();
				if (nextData) {
					handleAddProductToCart(nextData);
				}
			}
		},
		onError: () => {
			const productView = productRef.current.productView;

			addProductToCartSuccess();
			setShowAddToCartError({
				isCartError: true,
				errorMessage: 'Endpoint error. Product not added to cart.',
			});
			fireAnalytics('addItem', { success: false, productView });
			queueStatusRef = false;
			if (queueRef.length > 0) {
				const nextData = queueRef.shift();
				if (nextData) {
					handleAddProductToCart(nextData);
				}
			}
		},
	});

	const _addProductToCart = ({
		productView,
		quantity,
		openDrawer = true,
		vehicle,
	}: {
		productView: ProductCardView;
		quantity: number;
		openDrawer?: boolean;
		vehicle: Nullish<Vehicle>;
	}) => {
		queueStatusRef = true;
		const productItems = filterProductsView(productView, quantity, vehicle);
		if (productItems?.length && cartId && quantity) {
			openDrawer &&
				addProductToCart({
					product: productView,
					quantity,
					isAdded: false,
				});

			productRef.current.productView = productView;
			productRef.current.productItems = productItems;
			productRef.current.openDrawer = openDrawer;

			addProductsMutation.mutate({
				cartId: cartId,
				items: productItems.filter((product) => !!product),
			});
		} else {
			addProductToCartSuccess();
			setShowAddToCartError({
				isCartError: true,
				errorMessage:
					'Product not added to cart. No product quantity or cart id.',
			});
			fireAnalytics('addItem', { success: false, productView });
		}
	};

	const handleAddProductToCart = (data: CartItem) => {
		if (queueStatusRef) {
			queueRef.push(data);
		} else {
			_addProductToCart(data);
		}
	};

	return {
		addProductToCart: handleAddProductToCart,
		isSuccess: addProductsMutation.isSuccess,
		isError: addProductsMutation.isError,
		data: addProductsMutation.data,
	};
};
